import { ROUTES } from '@/app/routes';
import FadeIn from '@/components/motion/fade-in';
import Pressable from '@/components/motion/pressable';
import { CardDescription, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { $profile } from '@/entities/profile-entity';
import { cn } from '@/lib/utils';
import { useUnit } from 'effector-react';
import {
	AtSignIcon,
	BarChart3Icon,
	ChevronRightIcon,
	CigaretteIcon,
	CreditCardIcon,
	HammerIcon,
	SettingsIcon,
	UsersIcon,
	type LucideIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router';

interface MoreSheetProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

type ChartColor = 'chart-1' | 'chart-2' | 'chart-4' | 'chart-5';

interface MenuItem {
	label: string;
	description: string;
	path: string;
	icon: LucideIcon;
	color: ChartColor;
}

const MENU_ITEMS: MenuItem[] = [
	{
		label: 'Админ меню',
		description: 'Обзор и виджеты',
		path: ROUTES.admin,
		icon: CigaretteIcon,
		color: 'chart-2',
	},
	{
		label: 'Пользователи',
		description: 'Список, статистика, роли',
		path: ROUTES.adminUsers,
		icon: UsersIcon,
		color: 'chart-2',
	},
	{
		label: 'Статистика',
		description: 'Графики и аналитика',
		path: ROUTES.adminStats,
		icon: BarChart3Icon,
		color: 'chart-1',
	},
	{
		label: 'Настройки',
		description: 'Модули и параметры',
		path: ROUTES.adminSettings,
		icon: SettingsIcon,
		color: 'chart-4',
	},
	{
		label: 'Очередь билдера',
		description: 'Сборки, очередь и скип',
		path: ROUTES.adminBuilderQueue,
		icon: HammerIcon,
		color: 'chart-5',
	},
	{
		label: 'Автовбив',
		description: 'Карты, очередь и задачи',
		path: ROUTES.adminCardFill,
		icon: CreditCardIcon,
		color: 'chart-2',
	},
];

const COLOR_CLASSES: Record<ChartColor, { iconBg: string; title: string }> = {
	'chart-1': { iconBg: 'bg-chart-1/15', title: 'text-chart-1' },
	'chart-2': { iconBg: 'bg-chart-2/15', title: 'text-chart-2' },
	'chart-4': { iconBg: 'bg-chart-4/15', title: 'text-chart-4' },
	'chart-5': { iconBg: 'bg-chart-5/15', title: 'text-chart-5' },
};

export default function MoreSheet({ open, onOpenChange }: MoreSheetProps) {
	const navigate = useNavigate();
	const profile = useUnit($profile);

	function go(path: string) {
		navigate(path);
		onOpenChange(false);
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				showCloseButton={false}
				className='top-auto bottom-0 left-1/2 translate-x-[-50%] translate-y-0 w-full max-w-[460px] rounded-b-none rounded-t-2xl p-0 gap-0 overflow-hidden data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom data-[state=closed]:zoom-out-100 data-[state=open]:zoom-in-100'
			>
				<div className='px-4 pt-3 pb-[calc(1rem+env(safe-area-inset-bottom))] flex flex-col gap-4'>
					<div className='w-10 h-1 rounded-full bg-muted-foreground/25 mx-auto shrink-0' />

					<FadeIn delay={0.03}>
						<div className='flex items-center gap-3'>
							<div className='rounded-xl bg-muted/60 p-2.5 shrink-0'>
								<AtSignIcon size={20} />
							</div>
							<div className='min-w-0 flex-1'>
								<div className='text-base font-semibold truncate'>#{profile?.nickname}</div>
								<span className='inline-flex mt-1 rounded-full bg-chart-2/15 px-2.5 py-0.5 text-xs font-medium text-chart-2'>
									Admin
								</span>
							</div>
						</div>
					</FadeIn>

					<FadeIn delay={0.03}>
						<div className='flex flex-col gap-2'>
							<div className='text-xs font-medium text-muted-foreground uppercase tracking-wide px-0.5'>
								Администрирование
							</div>
							<div className='flex flex-col gap-2'>
								{MENU_ITEMS.map(item => {
									const Icon = item.icon;
									const colors = COLOR_CLASSES[item.color];
									return (
										<Pressable
											key={item.path}
											role='button'
											tabIndex={0}
											onClick={() => go(item.path)}
											className='rounded-xl border border-border bg-card shadow-sm overflow-hidden text-left active:bg-accent/30 cursor-pointer'
										>
											<div className='flex items-center justify-between gap-3 px-3 py-3'>
												<div className='flex items-center gap-3 min-w-0'>
													<div className={cn('rounded-lg p-2 shrink-0', colors.iconBg)}>
														<Icon className={colors.title} size={20} />
													</div>
													<div className='min-w-0'>
														<CardTitle className={cn('text-sm', colors.title)}>{item.label}</CardTitle>
														<CardDescription className='truncate'>{item.description}</CardDescription>
													</div>
												</div>
												<ChevronRightIcon size={18} className='text-muted-foreground shrink-0' />
											</div>
										</Pressable>
									);
								})}
							</div>
						</div>
					</FadeIn>
				</div>
			</DialogContent>
		</Dialog>
	);
}
